import { AxiosError } from 'axios';
import localforage from 'localforage';
import { makeAutoObservable, runInAction } from 'mobx';
import { makePersistable } from 'mobx-persist-store';
import { getSession, postLogin, postLogout, getSessionStatus } from 'src/api/csApi';
import { getUser } from 'src/api/stoApi';
import { PostLoginResponse } from '../models';

export type AuthError = 'PasswordWrong' | 'Unknown';

/** Store to persist and handle authentication. */
interface IAuthStore {
  username: string;
  phone: string;
  role: PostLoginResponse['role'];
  error: AuthError | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isRehydrated: boolean;

  /** Logs the user in. */
  login(username: string, password: string, platformProvider: string): Promise<void>;

  /** Logs the user out. */
  logout(): Promise<void>;
}

/** Implementation of an auth store. */
class AuthStore implements IAuthStore {
  username = '';
  phone = '';
  role: PostLoginResponse['role'] = '';
  error: AuthError | null = null;
  isAuthenticated = false;
  isLoading = false;
  isRehydrated = false;

  constructor() {
    this.init();
  }

  async login(username: string, password: string, platformProvider: string) {
    if (this.isLoading) {
      // bailout, noop
      return;
    }

    this.isLoading = true;

    try {
      const { role } = (await postLogin(username, password, platformProvider)).data.data;
      await getSessionStatus();
      await this.checkRole(role);
      const user = (await getUser()).data.data;
      runInAction(() => {
        this.error = null;
        this.username = username;
        this.role = role;
        this.phone = user.phone;
        this.isLoading = false;
        this.isAuthenticated = true;
      });
    } catch (error) {
      runInAction(() => {
        this.isLoading = false;
      });

      if (error instanceof AxiosError) {
        if (error.response?.status === 401) {
          this.reset('PasswordWrong');
        } else {
          this.reset('Unknown');
        }
      } else {
        this.reset('Unknown');
      }
    }
  }

  async logout() {
    await postLogout();
    this.reset(null);
  }

  /** Initializes the store. */
  private async init() {
    makeAutoObservable(this);
    try {
      await makePersistable(this, {
        name: 'auth',
        properties: ['username', 'phone', 'role'],
        storage: localforage,
      });
      if (!this.isRehydrated) {
        try {
          await getSession();
          await getSessionStatus();
          await this.checkRole(this.role);
          runInAction(() => {
            this.error = null;
            this.isAuthenticated = true;
            this.isLoading = false;
            this.isRehydrated = true;
          });
        } catch (error) {
          console.log('Error while trying to rehydrate auth', error);
          runInAction(() => {
            this.error = null;
            this.isAuthenticated = false;
            this.isLoading = false;
            this.isRehydrated = true;
          });
        }
      }
    } catch (error) {
      console.log(error);
    } finally {
      runInAction(() => {
        this.isRehydrated = true;
      });
    }
  }

  /** Resets the state. */
  private reset(error: AuthError | null) {
    this.error = error;
    this.isAuthenticated = false;
    this.isLoading = false;
  }

  /**
   * Checks if role is launchpad user.
   * @description Necessary as {@link getSessionStatus} only checks if the session is valid, but not if the role is correct.
   */
  private checkRole(role: string): void {
    const skipRoleCheck = (window as any).e2eTestConfigs?.skipLoginRoleCheck;
    if (!skipRoleCheck && role !== 'stoLaunchpadUser') throw new Error('Invalid role');
  }
}

/** Mocked auth store. */
class MockAuth implements IAuthStore {
  isAuthenticated = false;
  error: AuthError | null = null;
  isRehydrated = true;
  isLoading = false;
  phone = '';
  role: PostLoginResponse['role'] = '';
  username = '';

  constructor() {
    makeAutoObservable(this);
  }

  async init() {
    return;
  }

  async login() {
    return;
  }
  async logout() {
    return;
  }
}

let authStore: IAuthStore;
if (process.env.NODE_ENV === 'test') {
  authStore = new MockAuth() as any; // no localstorage support in node env
} else {
  authStore = new AuthStore();
}

// development, make auth available on window object...
(window as any).auth = authStore;

// singleton, exposes an instance by default
export { authStore };
